// Firebase configuration with TypeScript
import { initializeApp, FirebaseApp } from 'firebase/app';
import { 
  getAuth, 
  Auth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
  sendPasswordResetEmail,
  confirmPasswordReset
} from 'firebase/auth';
import { 
  getFirestore, 
  Firestore,
  doc,
  getDoc,
  collection,
  query,
  where,
  orderBy,
  getDocs
} from 'firebase/firestore';

// Firebase configuration type
interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

// Fallback configuration
const fallbackConfig: FirebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "AIzaSyApBhxcoirV4QDYRT_xy81nNVhDMjU6Cnk",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "eccast.firebaseapp.com",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "eccast",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "eccast.firebasestorage.app",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "872302780853",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "1:872302780853:web:8f260a34ecbd6293b3f9fc",
  measurementId: "G-EF2YD0EKVS"
};

// Initialize Firebase
let app: FirebaseApp;
let auth: Auth;
let firestore: Firestore;

try {
  app = initializeApp(fallbackConfig);
  auth = getAuth(app);
  firestore = getFirestore(app);
} catch (error) {
  console.error('❌ Firebase initialization failed:', error);
  throw error;
}

// Update Firebase config from server (optional)
async function updateFirebaseConfig(): Promise<void> {
  try {
    const apiUrl = process.env.REACT_APP_API_BASE_URL || '/api';
    const response = await fetch(`${apiUrl}/config/firebase`);
    
    if (!response.ok) {
      return;
    }
    
    const result = await response.json();
    
    if (result.success) {
    } else {
    }
  } catch (error) {
  }
}

// Run config update asynchronously
updateFirebaseConfig();

// Export Firebase instances
export { app, auth, firestore };

// Export Firebase Auth functions
export { sendPasswordResetEmail, confirmPasswordReset };

// User authentication functions
export const userAuth = {
  // Sign up with email and password
  async signUp(email: string, password: string, plan: 'free' | 'paid' | 'poc' = 'free', language: 'ja' | 'en' = 'ja') {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Note: User initialization is now handled by RegistrationComplete page
      // This ensures proper flow and error handling
      
      return { success: true, user };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  },
  
  // Sign in with email and password
  async signIn(email: string, password: string) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return { success: true, user: userCredential.user };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  },
  
  // Sign out
  async signOut() {
    try {
      await signOut(auth);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  },
  
  // Get current user
  getCurrentUser(): User | null {
    return auth.currentUser;
  },
  
  // Auth state observer
  onAuthStateChanged(callback: (user: User | null) => void) {
    return onAuthStateChanged(auth, callback);
  },
  
  // Get user token for API calls
  async getToken(): Promise<string | null> {
    const user = auth.currentUser;
    if (user) {
      return await user.getIdToken();
    }
    return null;
  },
  
  // Change password
  async changePassword(currentPassword: string, newPassword: string) {
    try {
      const user = auth.currentUser;
      
      if (!user || !user.email) {
        console.error('Firebase changePassword: No user or email found');
        return { success: false, error: 'auth/not-authenticated' };
      }

      // Re-authenticate with current password
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      
      // Update password
      await updatePassword(user, newPassword);
      
      return { success: true };
    } catch (error: any) {
      console.error('Firebase changePassword error:', error);
      return { success: false, error: error.code || error.message };
    }
  }
};

// User data management
export const userManager = {
  // Get user data from Firestore
  async getUserData(uid: string) {
    try {
      const userDoc = await getDoc(doc(firestore, 'users', uid));
      if (userDoc.exists()) {
        return { success: true, data: userDoc.data() };
      } else {
        return { success: false, error: 'User not found' };
      }
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  },
  
  // Get user credits
  async getCredits(uid: string) {
    try {
      const creditDoc = await getDoc(doc(firestore, 'credits', uid));
      if (creditDoc.exists()) {
        return { success: true, data: creditDoc.data() };
      } else {
        return { success: false, error: 'Credits not found' };
      }
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  },
  
  // Get user's credit transactions
  async getCreditTransactions(uid: string) {
    try {
      const q = query(
        collection(firestore, 'credit_transactions'),
        where('userId', '==', uid),
        orderBy('timestamp', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const transactions: any[] = [];
      
      querySnapshot.forEach((doc) => {
        transactions.push({ id: doc.id, ...doc.data() });
      });
      
      return { success: true, data: transactions };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }
};